import { ACTION_TYPE } from '../core/redux/actionType';
import { breakerHeldUntil, hasTrippedBreaker, updateBreaker } from '../core/redux/api/circuitBreaker/module';
import { apiGetProductHydrationErrors } from '../core/redux/api/endpoints';
import { addError, addLoading, getError, getLoading, removeLoadingAndError } from '../core/redux/api/module';
import { ApiReducer, makeApiAction, makeApiReducer, RsaaTypes } from '../core/redux/api/reduxStandardApiAction';
import { IAppState } from '../core/redux/IAppState';
import { assoc, isNilOrEmpty } from '../language/module';
import { getProductHydrationErrors, ProductHydrationErrorsResponse, toProductHydrationErrors } from './hydrationErrors';
import { getResource } from '../language/resources/getResource';
import { ApiError } from '../core/redux/api/apiError';
import { ResortMappingObject } from './productMappings';

const KEY_ACTION = ACTION_TYPE.PRODUCT_HYDRATION_ERRORS_REQUEST;
export const ACTIONS    = [
  ACTION_TYPE.PRODUCT_HYDRATION_ERRORS_REQUEST,
  ACTION_TYPE.PRODUCT_HYDRATION_ERRORS_RESPONSE,
  ACTION_TYPE.PRODUCT_HYDRATION_ERRORS_ERROR,
];


/**
 * Whether a hydration-error load is in flight for the given resort. This request is keyed by resort
 * abbreviation, not by action type, so read it through here rather than calling getLoading at the component.
 */
export const isProductHydrationErrorsLoading = (state: IAppState, resortAbbreviation: string): boolean =>
  !!getLoading(state, resortAbbreviation, KEY_ACTION);


/** Whether hydration errors for the given resort have landed since the last switch. */
export const haveProductHydrationErrorsLoaded = (state: IAppState, resortAbbreviation: string): boolean =>
  state.productHydrationErrorsLoadedFor === resortAbbreviation;


/** The given resort's last hydration-error load failure. Kept until the next request or a switch. */
export const getProductHydrationErrorsLoadError = (state: IAppState, resortAbbreviation: string): ApiError | undefined =>
  getError(state, resortAbbreviation, KEY_ACTION);


/** Whether the breaker is holding this request open, so a dispatch would bail out without firing. */
export const isProductHydrationErrorsSuppressed = (state: IAppState): boolean =>
  hasTrippedBreaker(state, KEY_ACTION);


/** The last moment the breaker holds this request, once it has tripped. Compare it with the clock. */
export const getProductHydrationErrorsHeldUntil = (state: IAppState): number | undefined =>
  breakerHeldUntil(state, KEY_ACTION);


/**
 * Makes an action that can be dispatched to get product hydration errors for the specified resort.
 */
export const getProductHydrationErrorsAction = (resortAbbreviation: string) => makeApiAction(
  {
    endpoint: apiGetProductHydrationErrors(resortAbbreviation),
    method:   'GET',
    headers:  (state: IAppState) => ({
      'content-type':  'application/json',
      'Authorization': `Bearer ${state.auth.bearerToken}`,
    }),
    types:    ACTIONS.map(action => ({ type: action, meta: { resortAbbreviation } })) as RsaaTypes,
    bailout:  (state: IAppState) => isProductHydrationErrorsSuppressed(state)
      || isProductHydrationErrorsLoading(state, resortAbbreviation),
  },
);


const reducer: ApiReducer<ProductHydrationErrorsResponse[]> = (state, action, error, isLoading, payload): IAppState => {
  const resort         = action.meta.resortAbbreviation;
  const appStateResort = state.selectedResort.resortAbbreviation;

  // Ignore the results if the response is for a previous query that didn't complete before the
  // user switched resorts  
  if (action.type === ACTION_TYPE.PRODUCT_HYDRATION_ERRORS_RESPONSE && !isNilOrEmpty(appStateResort) && appStateResort !== resort) {
    const withBreaker = updateBreaker(state, KEY_ACTION, !!error);
    return removeLoadingAndError(withBreaker, resort, KEY_ACTION);
  }

  if (isLoading) {
    return addLoading(state, resort, KEY_ACTION);
  }

  const withBreaker = updateBreaker(state, KEY_ACTION, !!error);

  if (error) {
    return addError(
      withBreaker,
      resort,
      KEY_ACTION,
      error ?? getResource(state.locale, 'GET_PRODUCT_HYDRATION_ERRORS_ACTION_ERROR'),
    );
  };

  const hydrationErrors = Array.isArray(payload) ? payload.map(toProductHydrationErrors) : [];

  const noLoadingOrError = removeLoadingAndError(withBreaker, resort, KEY_ACTION);

  // Attached in the same state that marks the load landed, so no render shows it landed with nothing attached.
  // Only hydrationErrors is replaced, so values typed mid-edit survive.
  const rows = state.resortMappingHistories;
  const withCounts: ResortMappingObject = {
    ...rows,
    productMappings: rows.productMappings.map(row => ({
      ...row,
      hydrationErrors: getProductHydrationErrors(hydrationErrors, row.entitlementTypeId, row.fulfillmentSeasonId, row.ageBin),
    })),
  };

  return assoc('productHydrationErrors', hydrationErrors,
    assoc('productHydrationErrorsLoadedFor', resort,
      assoc('resortMappingHistories', withCounts, noLoadingOrError)));
};


/**
 * Function that maps actions into new app state. Specifically, updates app state with information from actions related
 * to product hydration errors. Returns a new app state.
 */
export const getProductHydrationErrorsReducer = makeApiReducer<ProductHydrationErrorsResponse[]>(ACTIONS, KEY_ACTION, reducer);
